import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, Lock, LogOut, RefreshCw, ShieldCheck, Loader2, CheckCircle2, XCircle, Clock, Mail, Phone, User, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import {
  adminGetSlotsByDate,
  adminToggleSlot,
  adminBlockDay,
  adminDeleteConsultation,
  adminGetUpcomingBookings,
} from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

// ---- helpers ----
const toLocalISODate = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
};

const formatHu = (d) =>
  d.toLocaleDateString('hu-HU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

// ---- Login screen ----
const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setSubmitting(true);
    try {
      // Validate by trying to fetch today's slots
      await adminGetSlotsByDate(key.trim(), toLocalISODate(new Date()));
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
      toast.success('Bejelentkezve.');
    } catch (err) {
      toast.error('Érvénytelen admin kulcs.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 border border-gray-700 shadow-2xl"
      >
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <ShieldCheck className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Admin – Időpontkezelő
        </h1>
        <p className="text-sm text-gray-400 text-center mb-6">
          Add meg az admin kulcsodat a folytatáshoz. Csak ezen az eszközön tárolódik.
        </p>
        <Input
          data-testid="admin-key-input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="X-Admin-Key"
          className="bg-gray-900 border-gray-600 text-white placeholder-gray-500 mb-4"
          autoFocus
        />
        <Button
          data-testid="admin-login-submit"
          type="submit"
          disabled={submitting || !key.trim()}
          className="w-full bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-bold"
        >
          {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Lock className="mr-2" size={16} />}
          Bejelentkezés
        </Button>
      </form>
    </div>
  );
};

// ---- Slot tile ----
const SlotTile = ({ slot, onToggle, onShowBooking, busy }) => {
  const passBooked = !!slot.pass_booking_id;
  const booked = !!slot.consultation_id || passBooked;
  const b = slot.booking;
  let label, cls;
  if (booked) {
    label = passBooked ? 'Foglalt (bérlet)' : 'Foglalt';
    cls = 'bg-red-100 border-red-300 text-red-700 hover:bg-red-200';
  } else if (slot.available) {
    label = 'Elérhető';
    cls = 'bg-green-50 border-green-300 text-green-800 hover:bg-green-100';
  } else {
    label = 'Blokkolt';
    cls = 'bg-gray-200 border-gray-300 text-gray-600 hover:bg-gray-100';
  }
  return (
    <button
      data-testid={`admin-slot-${slot.time.replace(':', '')}`}
      type="button"
      disabled={busy}
      onClick={() => (booked ? onShowBooking(slot) : onToggle(slot))}
      className={`border rounded-lg px-3 py-3 text-sm font-semibold transition-all text-left ${cls}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-base">{slot.time}</span>
        {booked
          ? <Clock size={14} />
          : slot.available
            ? <CheckCircle2 size={14} />
            : <XCircle size={14} />}
      </div>
      <div className="text-[11px] uppercase tracking-wider mt-1 opacity-80">{label}</div>
      {b && (
        <div className="mt-1 text-[11px] font-bold truncate" title={b.name}>{b.name}</div>
      )}
      {b?.type === 'pass' && (
        <div className="text-[10px] font-normal truncate opacity-80" title={b.pass_title}>
          {b.pass_title}
        </div>
      )}
      {b?.type === 'consultation' && (
        <div className="text-[10px] font-normal truncate opacity-80">
          {b.meeting_mode === 'online'
            ? `Online · ${b.online_platform === 'messenger' ? 'Messenger' : 'Google Meet'}`
            : 'Személyes'}
        </div>
      )}
    </button>
  );
};

// ---- Foglalás részletei dialógus ----
const BookingDetailDialog = ({ slot, onClose }) => {
  const b = slot?.booking;
  const isPass = b?.type === 'pass';
  const row = (k, v) => (v ? (
    <div className="flex justify-between gap-3 py-1.5 border-t border-gray-100 text-sm">
      <span className="text-gray-500">{k}</span>
      <span className="text-gray-900 font-semibold text-right break-all">{v}</span>
    </div>
  ) : null);
  return (
    <Dialog open={!!slot} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="admin-booking-detail-dialog" className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-gray-900 text-lg font-bold">
            {slot?.date} · {slot?.time}
          </DialogTitle>
        </DialogHeader>
        {!b ? (
          <p className="text-sm text-gray-500">Ehhez az időponthoz nem találok foglalási adatot.</p>
        ) : (
          <div>
            <div className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-3 ${
              isPass ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {isPass ? 'Bérletes edzés' : 'Konzultáció'}
            </div>
            <div className="text-base font-bold text-gray-900">{b.name}</div>
            {row('E-mail', b.email)}
            {row('Telefon', b.phone)}
            {row('Állapot', b.status === 'pending' ? 'Foglalva (még nem jelölt)'
              : b.status === 'attended' ? 'Megtartva'
              : b.status === 'confirmed' ? 'Megerősítve'
              : b.status === 'awaiting_confirmation' ? 'Megerősítésre vár'
              : b.status)}
            {isPass && row('Bérlet', b.pass_title)}
            {isPass && row('Hátralévő alkalom',
              b.pass_remaining != null ? `${b.pass_remaining} / ${b.pass_total}` : null)}
            {isPass && row('Bérlet lejár', b.pass_expires_at ? b.pass_expires_at.slice(0, 10) : null)}
            {isPass && row('Megjegyzés', b.note)}
            {!isPass && row('Mód', b.meeting_mode === 'online'
              ? `Online · ${b.online_platform === 'messenger' ? 'Messenger' : 'Google Meet'}`
              : 'Személyes (BB Gym)')}
            {!isPass && row('Elérhetőség', b.online_contact)}
            {!isPass && row('Program', b.program_goal)}
            {!isPass && row('Hossz', b.duration_minutes ? `${b.duration_minutes} perc` : null)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---- Main page ----
const AdminSlotsPage = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [date, setDate] = useState(toLocalISODate(new Date()));
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bookingDetail, setBookingDetail] = useState(null);   // slot | null

  useDocumentMeta({ title: 'Admin – Időpontkezelő | Orosz Coaching' });

  const fetchSlots = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const data = await adminGetSlotsByDate(adminKey, date);
      setSlots(data.slots || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Az admin kulcs lejárt vagy érvénytelen. Jelentkezz be újra.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error('Időpontok betöltése sikertelen.');
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, date]);

  const fetchBookings = useCallback(async () => {
    if (!adminKey) return;
    try {
      const data = await adminGetUpcomingBookings(adminKey, 10);
      setBookings(data.bookings || []);
    } catch (_) {
      /* non-fatal */
    }
  }, [adminKey]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);
  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  if (!adminKey) {
    return <LoginScreen onAuth={setAdminKey} />;
  }

  const handleToggleSlot = async (slot) => {
    setBusy(true);
    try {
      await adminToggleSlot(adminKey, slot.id, !slot.available);
      toast.success(`${slot.time} → ${!slot.available ? 'Elérhető' : 'Blokkolt'}`);
      await fetchSlots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sikertelen toggle.');
    } finally {
      setBusy(false);
    }
  };

  const handleBlockDay = async (blocked) => {
    setBusy(true);
    try {
      const res = await adminBlockDay(adminKey, date, blocked);
      toast.success(`${blocked ? 'Blokkolva' : 'Felszabadítva'}: ${res.affected} időpont`);
      await fetchSlots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sikertelen művelet.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteBooking = async (b) => {
    if (!window.confirm(`Biztosan törlöd ${b.name} foglalását (${b.slot?.date} ${b.slot?.time})? Az idősáv felszabadul.`)) return;
    setBusy(true);
    try {
      await adminDeleteConsultation(adminKey, b.id);
      toast.success(`Törölve: ${b.name} — ${b.slot?.date} ${b.slot?.time}`);
      await Promise.all([fetchBookings(), fetchSlots()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A törlés nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey('');
    toast.success('Kijelentkezve.');
  };

  const dateObj = new Date(date + 'T00:00:00');
  const totalSlots = slots.length;
  const availableCount = slots.filter((s) => s.available && !s.consultation_id && !s.pass_booking_id).length;
  const bookedCount = slots.filter((s) => s.consultation_id || s.pass_booking_id).length;
  const blockedCount = totalSlots - availableCount - bookedCount;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header bar */}
      <header className="bg-gray-900 text-white sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-yellow-500" size={22} />
            <div>
              <div className="font-bold text-base sm:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Admin Időpontkezelő
              </div>
              <div className="text-[11px] text-gray-400 uppercase tracking-widest">Orosz Coaching</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-back-to-site"
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="bg-transparent border-gray-600 text-white hover:bg-gray-800 hidden sm:inline-flex"
            >
              Vissza az oldalra
            </Button>
            <Button
              data-testid="admin-logout"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="bg-transparent border-gray-600 text-white hover:bg-gray-800"
            >
              <LogOut size={14} className="mr-1.5" />
              Kijelentkezés
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-10 grid lg:grid-cols-3 gap-6">
        {/* Left: Day controls + slot grid */}
        <section className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-5">
              <div className="flex-1">
                <label className="block text-xs font-bold tracking-wider text-gray-500 uppercase mb-1.5">
                  Dátum
                </label>
                <div className="flex items-center gap-2">
                  <CalendarDays size={18} className="text-gray-400" />
                  <input
                    data-testid="admin-date-picker"
                    type="date"
                    value={date}
                    min={toLocalISODate(new Date(Date.now() - 86400000))}
                    onChange={(e) => setDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-base focus:border-yellow-500 focus:outline-none flex-1"
                  />
                </div>
                <div className="text-sm text-gray-600 mt-1.5 capitalize">{formatHu(dateObj)}</div>
              </div>
              <Button
                data-testid="admin-refresh"
                variant="outline"
                onClick={fetchSlots}
                disabled={loading}
                className="border-gray-300"
              >
                <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Frissítés
              </Button>
            </div>

            {/* Day actions */}
            <div className="flex flex-wrap gap-2 mb-5 pb-5 border-b border-gray-100">
              <Button
                data-testid="admin-block-day"
                onClick={() => handleBlockDay(true)}
                disabled={busy || availableCount === 0}
                className="bg-gray-900 hover:bg-gray-800 text-white text-sm"
              >
                <XCircle size={15} className="mr-1.5" />
                Egész nap blokkolása
              </Button>
              <Button
                data-testid="admin-unblock-day"
                onClick={() => handleBlockDay(false)}
                disabled={busy || blockedCount === 0}
                variant="outline"
                className="border-gray-300 text-sm"
              >
                <CheckCircle2 size={15} className="mr-1.5" />
                Egész nap felszabadítása
              </Button>
            </div>

            {/* Counters */}
            <div className="grid grid-cols-3 gap-2 mb-5 text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg py-2">
                <div className="text-2xl font-bold text-green-700">{availableCount}</div>
                <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">Elérhető</div>
              </div>
              <div className="bg-gray-100 border border-gray-200 rounded-lg py-2">
                <div className="text-2xl font-bold text-gray-700">{blockedCount}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">Blokkolt</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg py-2">
                <div className="text-2xl font-bold text-red-700">{bookedCount}</div>
                <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Foglalt</div>
              </div>
            </div>

            {/* Slots grid */}
            {loading ? (
              <div className="text-center py-10 text-gray-500">
                <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                Betöltés…
              </div>
            ) : slots.length === 0 ? (
              <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg">
                Nincs előgenerált időpont erre a napra (pl. vasárnap).
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="admin-slots-grid">
                {slots.map((s) => (
                  <SlotTile key={s.id} slot={s} onToggle={handleToggleSlot} onShowBooking={setBookingDetail} busy={busy} />
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4 leading-relaxed">
              <strong>Tipp:</strong> egy szabad időpontra koccintva válthatsz <em>Elérhető</em> és <em>Blokkolt</em> között.
              A piros, foglalt időpontra kattintva látod, ki és milyen bérlettel / konzultációval foglalta.
            </p>
          </div>
        </section>

        {/* Right: Upcoming bookings */}
        <aside className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-widest mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Legutóbbi foglalások
            </h2>
            {bookings.length === 0 ? (
              <p className="text-sm text-gray-500">Még nincs foglalás.</p>
            ) : (
              <ul className="space-y-3" data-testid="admin-bookings-list">
                {bookings.map((b) => (
                  <li key={b.id} className="border border-gray-100 rounded-lg p-3 hover:border-yellow-300 transition-colors">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="font-semibold text-gray-900 flex items-center gap-1.5 text-sm">
                        <User size={13} className="text-gray-400" />{b.name}
                      </div>
                      <div className="text-xs text-yellow-700 font-bold bg-yellow-50 px-2 py-0.5 rounded">
                        {b.slot?.date} • {b.slot?.time}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div className="flex items-center gap-1.5 truncate"><Mail size={11} />{b.email}</div>
                      <div className="flex items-center gap-1.5"><Phone size={11} />{b.phone}</div>
                      {b.program_goal && (
                        <div className="flex items-center gap-1.5 truncate"><Target size={11} />{b.program_goal}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid={`admin-booking-delete-${b.id}`}
                      disabled={busy}
                      onClick={() => handleDeleteBooking(b)}
                      className="mt-2 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      Foglalás törlése
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </main>

      <BookingDetailDialog slot={bookingDetail} onClose={() => setBookingDetail(null)} />
    </div>
  );
};

export default AdminSlotsPage;
